import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
    FaSearch, FaTasks, FaCalendarAlt, FaFlag, FaUser, FaBuilding, 
    FaCheckCircle, FaBell, FaClock, FaEdit, FaTrash, FaFilter, 
    FaSort, FaSortUp, FaSortDown, FaChevronLeft, FaChevronRight,
    FaCheckDouble, FaTimes, FaDownload, FaEye, FaHistory, FaUndo,
    FaChartBar, FaArchive, FaExclamationTriangle, FaUserPlus, FaUserCheck
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const Tasks = () => {
    const { user } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTasks, setSelectedTasks] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    
    // Sorting
    const [sortField, setSortField] = useState('due_date');
    const [sortOrder, setSortOrder] = useState('asc');
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [departmentFilter, setDepartmentFilter] = useState('all');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [showFilters, setShowFilters] = useState(false);
    const [showCompletedTasks, setShowCompletedTasks] = useState(false);
    
    // Snooze
    const [snoozeTaskId, setSnoozeTaskId] = useState(null);
    
    // Departments for filter
    const [departments, setDepartments] = useState([]);
    
    // Users for assignee filter
    const [users, setUsers] = useState([]);
    
    // Stats
    const [taskStats, setTaskStats] = useState({
        total: 0,
        pending: 0,
        completed: 0,
        overdue: 0,
        highPriority: 0
    });

    useEffect(() => {
        fetchTasks();
        fetchDepartments();
        fetchUsers();
    }, []);

    const fetchTasks = async () => {
        try {
            const response = await axios.get('/api/tasks');
            if (response.data.success) {
                setTasks(response.data.tasks);
                calculateStats(response.data.tasks);
            }
        } catch (error) {
            toast.error('Failed to load tasks');
        } finally {
            setLoading(false);
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await axios.get('/api/departments');
            if (response.data.success) {
                setDepartments(response.data.departments);
            }
        } catch (error) {
            console.error('Failed to load departments');
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get('/api/users');
            if (response.data && response.data.users) {
                setUsers(response.data.users);
            }
        } catch (error) {
            console.error('Failed to load users');
        }
    };

    const calculateStats = (tasksList) => {
        const now = new Date();
        const stats = {
            total: tasksList.length,
            pending: tasksList.filter(t => t.status === 'Pending').length,
            completed: tasksList.filter(t => t.status === 'Completed').length,
            overdue: tasksList.filter(t => t.status === 'Pending' && new Date(t.due_date) < now).length,
            highPriority: tasksList.filter(t => t.priority === 'High' && t.status === 'Pending').length
        };
        setTaskStats(stats);
    };

    // Handle stat card click - applies filter
    const handleStatClick = (filterType, value) => {
        setCurrentPage(1);
        setStatusFilter('all');
        
        switch(filterType) {
            case 'total':
                setStatusFilter('all');
                setPriorityFilter('all');
                setSearchTerm('');
                setDepartmentFilter('all');
                setDateRange({ start: '', end: '' });
                break;
            case 'pending':
                setStatusFilter('pending');
                break;
            case 'completed':
                setStatusFilter('completed');
                if (!isAdmin && !showCompletedTasks) {
                    setShowCompletedTasks(true);
                }
                break;
            case 'overdue':
                setStatusFilter('overdue');
                break;
            case 'highPriority':
                setPriorityFilter('high');
                break;
            default:
                break;
        }
    };

    const updateTaskStatus = async (taskId, status) => {
        try {
            await axios.patch(`/api/tasks/${taskId}/status`, { status });
            toast.success(`Task marked as ${status}`);
            fetchTasks();
            setSelectedTasks(new Set());
            setSelectAll(false);
        } catch (error) {
            toast.error('Failed to update task status');
        }
    };

    const deleteTask = async (taskId) => {
        if (window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
            try {
                await axios.delete(`/api/tasks/${taskId}`);
                toast.success('Task deleted successfully');
                fetchTasks();
                setSelectedTasks(new Set());
                setSelectAll(false);
            } catch (error) {
                toast.error('Failed to delete task');
            }
        }
    };

    const bulkDelete = async () => {
        if (selectedTasks.size === 0) {
            toast.error('No tasks selected');
            return;
        }
        
        if (window.confirm(`Are you sure you want to delete ${selectedTasks.size} task(s)? This action cannot be undone.`)) {
            try {
                const promises = Array.from(selectedTasks).map(taskId => 
                    axios.delete(`/api/tasks/${taskId}`)
                );
                await Promise.all(promises);
                toast.success(`${selectedTasks.size} task(s) deleted successfully`);
                fetchTasks();
                setSelectedTasks(new Set());
                setSelectAll(false);
            } catch (error) {
                toast.error('Failed to delete some tasks');
            }
        }
    };

    const bulkUpdateStatus = async (status) => {
        if (selectedTasks.size === 0) {
            toast.error('No tasks selected');
            return;
        }
        
        try {
            const promises = Array.from(selectedTasks).map(taskId => 
                axios.patch(`/api/tasks/${taskId}/status`, { status })
            );
            await Promise.all(promises);
            toast.success(`${selectedTasks.size} task(s) marked as ${status}`);
            fetchTasks();
            setSelectedTasks(new Set());
            setSelectAll(false);
        } catch (error) {
            toast.error('Failed to update some tasks');
        }
    };

    const editTask = (taskId) => {
        window.location.href = `/edit-task/${taskId}`;
    };

    const viewTaskDetails = (taskId) => {
        window.location.href = `/task/${taskId}`;
    };

    const snoozeTask = async (taskId, days) => {
        try {
            const task = tasks.find(t => t.task_id === taskId);
            if (!task) return;
            
            const newDueDate = new Date(task.due_date);
            newDueDate.setDate(newDueDate.getDate() + days);
            
            await axios.put(`/api/tasks/${taskId}`, {
                ...task,
                dueDate: newDueDate.toISOString().split('T')[0]
            });
            
            toast.success(`Task snoozed for ${days} day(s)`);
            setSnoozeTaskId(null);
            fetchTasks();
        } catch (error) {
            toast.error('Failed to snooze task');
        }
    };

    const exportTasks = () => {
        if (filteredAndSortedTasks.length === 0) {
            toast.error('No tasks to export');
            return;
        }
        
        const exportData = filteredAndSortedTasks.map(task => ({
            'Task ID': task.task_id,
            'Title': task.title,
            'Description': task.description,
            'Assigned To': task.assigned_to_name || task.assigned_user || 'Unassigned',
            'Status': task.status,
            'Priority': task.priority,
            'Due Date': task.due_date,
            'Department': task.department,
            'Created Date': task.created_date,
            'Completed Date': task.completed_date || 'N/A'
        }));
        
        const headers = Object.keys(exportData[0]).join(',');
        const rows = exportData.map(row => Object.values(row).map(value => `"${value || ''}"`).join(',')).join('\n');
        const csv = `${headers}\n${rows}`;
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tasks_export_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Tasks exported successfully');
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedTasks(new Set());
        } else {
            const newSelected = new Set(paginatedTasks.map(task => task.task_id));
            setSelectedTasks(newSelected);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectTask = (taskId) => {
        const newSelected = new Set(selectedTasks);
        if (newSelected.has(taskId)) {
            newSelected.delete(taskId);
        } else {
            newSelected.add(taskId);
        }
        setSelectedTasks(newSelected);
        setSelectAll(newSelected.size === paginatedTasks.length && paginatedTasks.length > 0);
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
        setCurrentPage(1);
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return <FaSort className="text-gray-400" />;
        return sortOrder === 'asc' ? <FaSortUp className="text-purple-500" /> : <FaSortDown className="text-purple-500" />;
    };

    const isOverdue = (dueDate) => {
        return new Date(dueDate) < new Date();
    };

    const getPriorityColor = (priority) => {
        switch(priority) {
            case 'High': return 'priority-high';
            case 'Medium': return 'priority-medium';
            case 'Low': return 'priority-low';
            default: return 'priority-medium';
        }
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'Completed': return 'status-completed';
            case 'Pending': return 'status-pending';
            default: return 'status-pending';
        }
    };

    // Apply all filters with completed tasks visibility logic
    const filteredAndSortedTasks = useMemo(() => {
        let filtered = tasks.filter(task => {
            if (!task) return false;
            
            const isAdminUser = user?.role === 'Admin';
            
            if (task.status === 'Completed') {
                if (!isAdminUser && !showCompletedTasks) {
                    return false;
                }
            }
            
            // Search filter
            const matchesSearch = (task.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (task.description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (task.assigned_to_name?.toLowerCase() || '').includes(searchTerm.toLowerCase());
            
            // Status filter
            let matchesStatus = true;
            if (statusFilter === 'pending') {
                matchesStatus = task.status === 'Pending';
            } else if (statusFilter === 'completed') {
                matchesStatus = task.status === 'Completed';
            } else if (statusFilter === 'overdue') {
                matchesStatus = task.status === 'Pending' && isOverdue(task.due_date);
            }
            
            // Priority filter
            let matchesPriority = true;
            if (priorityFilter !== 'all') {
                matchesPriority = (task.priority?.toLowerCase() || '') === priorityFilter.toLowerCase();
            }
            
            // Department filter
            let matchesDepartment = true;
            if (departmentFilter !== 'all') {
                matchesDepartment = (task.department || '') === departmentFilter;
            }
            
            // Date range filter
            let matchesDateRange = true;
            if (dateRange.start) {
                matchesDateRange = matchesDateRange && new Date(task.due_date) >= new Date(dateRange.start);
            }
            if (dateRange.end) {
                matchesDateRange = matchesDateRange && new Date(task.due_date) <= new Date(dateRange.end);
            }
            
            return matchesSearch && matchesStatus && matchesPriority && matchesDepartment && matchesDateRange;
        });

        // Apply sorting
        filtered.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];
            
            if (sortField === 'due_date' || sortField === 'created_date') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            } else if (sortField === 'assigned_to_name') {
                aVal = a.assigned_to_name || '';
                bVal = b.assigned_to_name || '';
            }
            
            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }
            
            if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
        
        return filtered;
    }, [tasks, searchTerm, statusFilter, priorityFilter, departmentFilter, dateRange, sortField, sortOrder, user?.role, showCompletedTasks]);

    // Pagination
    const totalPages = Math.ceil(filteredAndSortedTasks.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedTasks = filteredAndSortedTasks.slice(startIndex, startIndex + itemsPerPage);

    const resetFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setPriorityFilter('all');
        setDepartmentFilter('all');
        setDateRange({ start: '', end: '' });
        setCurrentPage(1);
        if (user?.role !== 'Admin') {
            setShowCompletedTasks(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <div className="loader"></div>
            </div>
        );
    }

    const isAdmin = user?.role === 'Admin';
    
    const getActiveFilterText = () => {
        if (statusFilter === 'pending') return 'Pending Tasks';
        if (statusFilter === 'completed') return 'Completed Tasks';
        if (statusFilter === 'overdue') return 'Overdue Tasks';
        if (priorityFilter === 'high') return 'High Priority Tasks';
        return 'All Tasks';
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2">
                        <FaTasks className="text-purple-500" />
                        My Tasks
                    </h1>
                    <p className="text-gray-500 mt-1">Manage and track your tasks efficiently</p>
                    {statusFilter !== 'all' && (
                        <div className="mt-2">
                            <span className="inline-flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                                <FaFilter size={12} />
                                Filtering: {getActiveFilterText()}
                                <button 
                                    onClick={() => setStatusFilter('all')}
                                    className="ml-1 hover:text-purple-900"
                                >
                                    <FaTimes size={12} />
                                </button>
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex gap-3 flex-wrap">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                            showFilters ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        <FaFilter /> Filters
                    </button>
                    <button
                        onClick={exportTasks}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all flex items-center gap-2"
                    >
                        <FaDownload /> Export
                    </button>
                    {selectedTasks.size > 0 && (
                        <>
                            <button
                                onClick={() => bulkUpdateStatus('Completed')}
                                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all flex items-center gap-2"
                            >
                                <FaCheckDouble /> Complete ({selectedTasks.size})
                            </button>
                            <button
                                onClick={bulkDelete}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all flex items-center gap-2"
                            >
                                <FaTrash /> Delete ({selectedTasks.size})
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Stats Cards - Clickable */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div 
                    onClick={() => handleStatClick('total')}
                    className={`bg-white rounded-xl shadow-sm p-4 border-l-4 border-blue-500 cursor-pointer transition-all hover:shadow-md hover:scale-105 ${
                        statusFilter === 'all' && priorityFilter !== 'high' ? 'ring-2 ring-blue-500 shadow-lg' : ''
                    }`}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs">Total Tasks</p>
                            <p className="text-2xl font-bold">{taskStats.total}</p>
                        </div>
                        <FaTasks className="text-blue-500 text-2xl opacity-50" />
                    </div>
                </div>
                
                <div 
                    onClick={() => handleStatClick('pending')}
                    className={`bg-white rounded-xl shadow-sm p-4 border-l-4 border-yellow-500 cursor-pointer transition-all hover:shadow-md hover:scale-105 ${
                        statusFilter === 'pending' ? 'ring-2 ring-yellow-500 shadow-lg' : ''
                    }`}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs">Pending</p>
                            <p className="text-2xl font-bold text-yellow-600">{taskStats.pending}</p>
                        </div>
                        <FaClock className="text-yellow-500 text-2xl opacity-50" />
                    </div>
                </div>
                
                <div 
                    onClick={() => handleStatClick('completed')}
                    className={`bg-white rounded-xl shadow-sm p-4 border-l-4 border-green-500 cursor-pointer transition-all hover:shadow-md hover:scale-105 ${
                        statusFilter === 'completed' ? 'ring-2 ring-green-500 shadow-lg' : ''
                    }`}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs">Completed</p>
                            <p className="text-2xl font-bold text-green-600">{taskStats.completed}</p>
                        </div>
                        <FaCheckCircle className="text-green-500 text-2xl opacity-50" />
                    </div>
                </div>
                
                <div 
                    onClick={() => handleStatClick('overdue')}
                    className={`bg-white rounded-xl shadow-sm p-4 border-l-4 border-red-500 cursor-pointer transition-all hover:shadow-md hover:scale-105 ${
                        statusFilter === 'overdue' ? 'ring-2 ring-red-500 shadow-lg' : ''
                    }`}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs">Overdue</p>
                            <p className="text-2xl font-bold text-red-600">{taskStats.overdue}</p>
                        </div>
                        <FaExclamationTriangle className="text-red-500 text-2xl opacity-50" />
                    </div>
                </div>
                
                <div 
                    onClick={() => handleStatClick('highPriority')}
                    className={`bg-white rounded-xl shadow-sm p-4 border-l-4 border-purple-500 cursor-pointer transition-all hover:shadow-md hover:scale-105 ${
                        priorityFilter === 'high' && statusFilter === 'all' ? 'ring-2 ring-purple-500 shadow-lg' : ''
                    }`}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs">High Priority</p>
                            <p className="text-2xl font-bold text-purple-600">{taskStats.highPriority}</p>
                        </div>
                        <FaFlag className="text-purple-500 text-2xl opacity-50" />
                    </div>
                </div>
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && (
                <div className="bg-white rounded-xl shadow-sm p-6 mb-6 animate-fade-in-up border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Search</label>
                            <div className="relative">
                                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by title, description or assignee..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="completed">Completed</option>
                                <option value="overdue">Overdue</option>
                            </select>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
                            <select
                                value={priorityFilter}
                                onChange={(e) => setPriorityFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="all">All Priorities</option>
                                <option value="high">High</option>
                                <option value="medium">Medium</option>
                                <option value="low">Low</option>
                            </select>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Department</label>
                            <select
                                value={departmentFilter}
                                onChange={(e) => setDepartmentFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="all">All Departments</option>
                                {departments.map(dept => (
                                    <option key={dept} value={dept}>{dept}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">From Date</label>
                            <input
                                type="date"
                                value={dateRange.start}
                                onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">To Date</label>
                            <input
                                type="date"
                                value={dateRange.end}
                                onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        
                        <div className="flex items-end gap-2">
                            <button
                                onClick={resetFilters}
                                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all flex items-center gap-2"
                            >
                                <FaTimes /> Reset
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Completed Tasks Toggle */}
            {!isAdmin && taskStats.completed > 0 && (
                <div className="mb-4 flex justify-end">
                    <button
                        onClick={() => setShowCompletedTasks(!showCompletedTasks)}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                            showCompletedTasks 
                                ? 'bg-purple-500 text-white' 
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        <FaArchive />
                        {showCompletedTasks ? 'Hide' : 'Show'} Completed Tasks ({taskStats.completed})
                    </button>
                </div>
            )}

            {/* Admin Info Banner */}
            {isAdmin && taskStats.completed > 0 && (
                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700 flex items-center gap-2">
                    <FaEye />
                    Admin View: You can see all tasks including completed ones. Use filters to narrow down.
                </div>
            )}

            {/* Results Summary */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                <p className="text-sm text-gray-500">
                    Showing {filteredAndSortedTasks.length > 0 ? startIndex + 1 : 0} to {Math.min(startIndex + itemsPerPage, filteredAndSortedTasks.length)} of {filteredAndSortedTasks.length} tasks
                </p>
                <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-500">Show:</label>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                        className="px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
            </div>

            {/* Tasks Table */}
            {paginatedTasks.length > 0 ? (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 w-10">
                                        <input
                                            type="checkbox"
                                            checked={selectAll && paginatedTasks.length > 0}
                                            onChange={handleSelectAll}
                                            className="w-4 h-4 rounded border-gray-300 focus:ring-purple-500"
                                        />
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('title')}>
                                        <div className="flex items-center gap-1">Task {getSortIcon('title')}</div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('assigned_to_name')}>
                                        <div className="flex items-center gap-1">Assigned To {getSortIcon('assigned_to_name')}</div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('due_date')}>
                                        <div className="flex items-center gap-1">Due Date {getSortIcon('due_date')}</div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('priority')}>
                                        <div className="flex items-center gap-1">Priority {getSortIcon('priority')}</div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')}>
                                        <div className="flex items-center gap-1">Status {getSortIcon('status')}</div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {paginatedTasks.map(task => (
                                    <tr key={task.task_id} className={`hover:bg-gray-50 transition-colors ${task.status === 'Completed' ? 'bg-green-50/30' : ''}`}>
                                        <td className="px-4 py-4">
                                            <input
                                                type="checkbox"
                                                checked={selectedTasks.has(task.task_id)}
                                                onChange={() => handleSelectTask(task.task_id)}
                                                className="w-4 h-4 rounded border-gray-300 focus:ring-purple-500"
                                                disabled={task.status === 'Completed' && !isAdmin}
                                            />
                                        </td>
                                        <td className="px-4 py-4">
                                            <div>
                                                <div className="font-medium text-gray-900">{task.title}</div>
                                                <div className="text-sm text-gray-500 mt-1 max-w-md truncate">{task.description}</div>
                                                {task.department && (
                                                    <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                                        <FaBuilding /> {task.department}
                                                    </div>
                                                )}
                                                {task.completed_date && (
                                                    <div className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                                        <FaCheckCircle /> Completed: {formatDate(task.completed_date)}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                                    <FaUserCheck className="text-purple-600 text-sm" />
                                                </div>
                                                <div>
                                                    <div className="font-medium text-gray-900">
                                                        {task.assigned_to_name || task.assigned_user || 'Unassigned'}
                                                    </div>
                                                    {task.assigned_mobile && (
                                                        <div className="text-xs text-gray-400">{task.assigned_mobile}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-1">
                                                <FaCalendarAlt className="text-gray-400" />
                                                <span className={isOverdue(task.due_date) && task.status === 'Pending' ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                                                    {formatDate(task.due_date)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className={getPriorityColor(task.priority)}>
                                                <FaFlag className="inline mr-1" size={12} /> {task.priority}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className={getStatusColor(task.status)}>
                                                {task.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="flex gap-2">
                                                {task.status === 'Pending' && (
                                                    <>
                                                        <button
                                                            onClick={() => updateTaskStatus(task.task_id, 'Completed')}
                                                            className="p-1 text-green-600 hover:text-green-800 transition-colors"
                                                            title="Mark Complete"
                                                        >
                                                            <FaCheckCircle size={18} />
                                                        </button>
                                                        <div className="relative">
                                                            <button
                                                                onClick={() => setSnoozeTaskId(snoozeTaskId === task.task_id ? null : task.task_id)}
                                                                className="p-1 text-orange-600 hover:text-orange-800 transition-colors"
                                                                title="Snooze"
                                                            >
                                                                <FaBell size={18} />
                                                            </button>
                                                            {snoozeTaskId === task.task_id && (
                                                                <div className="absolute top-full mt-1 right-0 bg-white rounded-lg shadow-lg p-2 z-10 min-w-[120px] border border-gray-200">
                                                                    {[1, 2, 3, 5, 7].map(days => (
                                                                        <button
                                                                            key={days}
                                                                            onClick={() => snoozeTask(task.task_id, days)}
                                                                            className="w-full px-3 py-1 text-left hover:bg-gray-100 rounded text-sm"
                                                                        >
                                                                            {days} day{days > 1 ? 's' : ''}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                                <button
                                                    onClick={() => editTask(task.task_id)}
                                                    className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                                                    title="Edit"
                                                >
                                                    <FaEdit size={18} />
                                                </button>
                                                <button
                                                    onClick={() => viewTaskDetails(task.task_id)}
                                                    className="p-1 text-purple-600 hover:text-purple-800 transition-colors"
                                                    title="View Details"
                                                >
                                                    <FaEye size={18} />
                                                </button>
                                                {isAdmin && (
                                                    <button
                                                        onClick={() => deleteTask(task.task_id)}
                                                        className="p-1 text-red-600 hover:text-red-800 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <FaTrash size={18} />
                                                    </button>
                                                )}
                                                {task.status === 'Completed' && isAdmin && (
                                                    <button
                                                        onClick={() => updateTaskStatus(task.task_id, 'Pending')}
                                                        className="p-1 text-yellow-600 hover:text-yellow-800 transition-colors"
                                                        title="Reopen Task"
                                                    >
                                                        <FaUndo size={18} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 px-6 py-4 border-t">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 bg-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-300 transition-colors flex items-center gap-1"
                            >
                                <FaChevronLeft /> Previous
                            </button>
                            <div className="flex gap-2 flex-wrap justify-center">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`px-3 py-1 rounded-lg transition-colors ${
                                                currentPage === pageNum
                                                    ? 'bg-purple-500 text-white'
                                                    : 'bg-gray-200 hover:bg-gray-300'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 bg-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-300 transition-colors flex items-center gap-1"
                            >
                                Next <FaChevronRight />
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <FaTasks className="mx-auto text-5xl text-gray-300 mb-3" />
                    <p className="text-gray-500">No tasks found</p>
                    <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
                    {(searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' || departmentFilter !== 'all' || dateRange.start || dateRange.end) && (
                        <button
                            onClick={resetFilters}
                            className="mt-4 text-purple-500 hover:text-purple-600 flex items-center gap-2 mx-auto"
                        >
                            <FaTimes /> Clear all filters
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default Tasks;